import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Cloth.css";

interface ClothItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Cloth: React.FC = () => {
  const [data, setData] = useState<ClothItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/cloth`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching cloth:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: ClothItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: ClothItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Clothing",
    de: "Kleidung",
    ta: "ஆடை",
    si: "ඇඳුම්",
    hi: "कपड़े",
    ar: "ملابس",
    tr: "Giyim",
    ru: "Одежда",
    zh: "服装",
    fr: "Vêtements",
    es: "Ropa",
  };

  const subtitleMap = {
    en: "Clothing Items in German",
    de: "Kleidungsstücke auf Deutsch",
    ta: "ஜெர்மன் மொழியில் ஆடை பொருட்கள்",
    si: "ජර්මන් භාෂාවෙන් ඇඳුම් භාණ්ඩ",
    hi: "जर्मन में कपड़ों की वस्तुएँ",
    ar: "ملابس بالألمانية",
    tr: "Almanca'da Giyim Eşyaları",
    ru: "Одежда на немецком",
    zh: "德语服装",
    fr: "Vêtements en allemand",
    es: "Ropa en alemán",
  };

  return (
    <div className="cloth-page-container">
      <div className="cloth-page-header">
        <div>
          <h1 className="cloth-page-title">{titleMap["de"]}</h1>
          <p className="cloth-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="cloth-page-loading">Loading...</div>
      ) : (
        <div className="cloth-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`cloth-page-card ${
                  isPlaying ? "cloth-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="cloth-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="cloth-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="cloth-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="cloth-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="cloth-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Cloth;
